import React from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import NewsArticleLayout from "@/components/NewsArticleLayout";

const CDBSGuidePage = () => {
  return (
    <NewsArticleLayout
      title="Does My Child Qualify for Free Dental? A Complete Guide to the Child Dental Benefits Schedule (CDBS) for Northern Melbourne Families"
      date="April 2026"
      author="Dr Fatima Kurnaz"
    >
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 space-y-2 text-neutral-800">
        <p className="font-semibold text-neutral-900">Key Takeaways</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Children aged 2–17 who receive Family Tax Benefit Part A (or certain other government payments) may be eligible for the Child Dental Benefits Schedule (CDBS).</li>
          <li>Eligible children can access up to <strong>$1,158 in government-funded dental benefits</strong> over two consecutive calendar years under the current benefit period.</li>
          <li>The CDBS covers check-ups, X-rays, scale and cleans, fillings, and extractions — but does <strong>not</strong> cover orthodontics or cosmetic procedures.</li>
          <li>Y3 Smiles Dental is a participating CDBS provider serving families across Broadmeadows, Glenroy, Campbellfield, Craigieburn, Meadow Heights, and surrounding suburbs.</li>
        </ul>
      </div>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Introduction</h2>
        <p>
          Picture this: a parent in Broadmeadows is sitting in a waiting room, and another parent mentions almost in
          passing that their child just had two fillings, a scale and clean, and a full set of X-rays — all at no
          out-of-pocket cost. The first parent is stunned. Their own child, the same age, had never been to the dentist.
          They hadn't known there was a government benefit that could cover it.
        </p>
        <p>
          This scenario plays out across Melbourne's northern suburbs every single day. Thousands of families in
          Broadmeadows, Glenroy, Campbellfield, Craigieburn, and Meadow Heights are eligible for the{" "}
          <strong>Child Dental Benefits Schedule (CDBS)</strong> — a federal government program that provides meaningful
          dental funding for children — yet never use it, simply because no one told them about it.
        </p>
        <p>
          If you have a child aged 2–17 and receive Family Tax Benefit Part A or certain other Centrelink payments, there
          is a good chance your child qualifies. This guide will walk you through exactly how it works, what is covered,
          how to check eligibility, and what to expect when you bring your child to Y3 Smiles Dental for a CDBS
          appointment.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">What Is the Child Dental Benefits Schedule (CDBS)?</h2>
        <p>
          The Child Dental Benefits Schedule is a federal government initiative administered through Medicare. It was
          designed to help Australian families access preventive and basic dental care for children without the full
          financial burden of private dental fees.
        </p>

        <h3 className="text-xl font-semibold">Who administers it?</h3>
        <p>
          Services Australia (Medicare) manages the CDBS on behalf of the federal government. There is no separate
          application form — eligibility is assessed automatically based on your family's Medicare and Centrelink
          records.
        </p>

        <h3 className="text-xl font-semibold">What does it cover?</h3>
        <p>The CDBS covers a range of essential dental services, including:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Dental examinations and check-ups</li>
          <li>Dental X-rays</li>
          <li>Scale and clean (professional cleaning)</li>
          <li>Fluoride treatments</li>
          <li>Fissure sealants</li>
          <li>Fillings (tooth-coloured and amalgam)</li>
          <li>Root canal treatment (in eligible circumstances)</li>
          <li>Extractions</li>
        </ul>

        <h3 className="text-xl font-semibold">What is NOT covered?</h3>
        <p>It is important to understand that the CDBS does <strong>not</strong> cover:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Orthodontic treatment (braces, aligners, retainers)</li>
          <li>Cosmetic dental procedures</li>
          <li>Dental services provided in a hospital setting</li>
          <li>Dentures or complex prosthetics</li>
        </ul>

        <h3 className="text-xl font-semibold">Where can I use it?</h3>
        <p>
          One of the most important points to understand is that the CDBS is not limited to community dental clinics or
          public hospital waitlists. Eligible families can use their CDBS benefits at{" "}
          <strong>participating private dental practices</strong> — including Y3 Smiles Dental in Broadmeadows. This
          means your child can receive care in a modern, comfortable private clinic environment without needing to wait
          months for a public appointment.
        </p>
      </section>

      <figure className="my-6">
        <img
          src="/kids-dentistry.avif"
          alt="A young child sitting comfortably in a dental chair during a routine check-up at Y3 Smiles Dental — children's dentistry and CDBS bulk billing in Broadmeadows"
          className="rounded-2xl w-full object-cover"
        />
        <figcaption className="text-sm text-neutral-500 mt-2 text-center">
          Children's dental check-up at Y3 Smiles Dental, Broadmeadows
        </figcaption>
      </figure>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">How Much Can Your Child Receive?</h2>
        <p>
          Under the current CDBS benefit period, eligible children can access up to{" "}
          <strong>$1,158 in dental benefits</strong> over two consecutive calendar years.
        </p>
        <p>To put that in practical terms for a northern Melbourne family:</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-primary/10">
                <th className="text-left p-3 font-semibold border border-primary/20">Service</th>
                <th className="text-left p-3 font-semibold border border-primary/20">Indicative CDBS Benefit</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Dental check-up", "Covered under CDBS"],
                ["Scale and clean", "Covered under CDBS"],
                ["Full-mouth X-rays", "Covered under CDBS"],
                ["Tooth-coloured filling", "Covered under CDBS"],
                ["Tooth extraction", "Covered under CDBS"],
              ].map(([service, benefit]) => (
                <tr key={service} className="even:bg-neutral-50">
                  <td className="p-3 border border-primary/20">{service}</td>
                  <td className="p-3 border border-primary/20">{benefit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          At Y3 Smiles Dental, <strong>kids check &amp; clean starts from $99</strong>. For many families with eligible
          children, CDBS benefits will cover this entirely — and there may be significant benefit remaining for further
          treatment such as fillings or X-rays within the same benefit period.
        </p>
        <p>
          Where the total cost of treatment exceeds the available CDBS benefit, a gap amount may apply. Our team will
          always provide a written quote before any treatment begins so there are no surprises. And if there is a gap,
          Afterpay is available to help spread the cost into manageable payments.
        </p>
        <p>
          The key takeaway: for families across Broadmeadows, Glenroy, Campbellfield, and surrounds who qualify, the
          CDBS represents a genuine and significant opportunity to give your child a healthy start in life — at little
          or no out-of-pocket cost.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">How to Check If Your Child Is Eligible</h2>
        <p>
          Checking CDBS eligibility is straightforward, and you do not need a referral from a GP.
        </p>

        <h3 className="text-xl font-semibold">Step 1 — Check via Medicare online or myGov</h3>
        <p>
          Log in to your myGov account and navigate to your Medicare section. Under "My Health," look for "Child Dental
          Benefits Schedule." Your child's eligibility status and remaining balance will be displayed there.
        </p>

        <h3 className="text-xl font-semibold">Step 2 — Call Services Australia</h3>
        <p>
          If you prefer to speak with someone, call Services Australia on <strong>132 011</strong> (Medicare general
          enquiries). Have your Medicare card and your child's date of birth ready.
        </p>

        <h3 className="text-xl font-semibold">Step 3 — Let Y3 Smiles confirm it on the day</h3>
        <p>
          If you are unsure, simply book an appointment. Y3 Smiles Dental has HICAPS on-site, which means we can verify
          your child's CDBS eligibility and process the Medicare claim in real time — no paperwork, no waiting, no bulk
          billing gap forms to fill in at home. Our reception team can also do a preliminary check at the time of booking
          if you prefer.
        </p>

        <h3 className="text-xl font-semibold">What to bring to your child's first CDBS appointment:</h3>
        <ul className="list-disc pl-6 space-y-2">
          <li>Your Medicare card (or the child's individual Medicare card if they have one)</li>
          <li>Any Health Care Card or Centrelink paperwork if applicable</li>
          <li>A list of any concerns about your child's teeth</li>
        </ul>
        <p>
          Our clinics are conveniently located for families across{" "}
          <strong>Broadmeadows, Glenroy, Campbellfield, Craigieburn, Meadow Heights, and Coolaroo</strong>, with easy
          parking and public transport access.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">What Happens at a CDBS Appointment at Y3 Smiles?</h2>
        <p>
          We know that for many children, a visit to the dentist can feel unfamiliar or even a little daunting. At Y3
          Smiles Dental, we work hard to make every child's appointment calm, gentle, and even enjoyable.
        </p>

        <h3 className="text-xl font-semibold">Arrival and welcome</h3>
        <p>
          You will be greeted by our friendly reception team. If you have not visited before, we will take a brief health
          history for your child. HICAPS claims for CDBS are processed at check-in, so there is no end-of-appointment
          paperwork.
        </p>

        <h3 className="text-xl font-semibold">The examination</h3>
        <p>
          Our dentists take a gentle, no-rush approach with children. The examination includes a thorough check of all
          teeth, gums, jaw development, and bite. We use child-appropriate language to explain what we are doing at every
          step — helping children feel informed rather than anxious.
        </p>

        <h3 className="text-xl font-semibold">X-rays (if indicated)</h3>
        <p>
          Dental X-rays help us identify issues that are not visible to the naked eye, such as decay between teeth or
          developing issues with adult teeth. We only take X-rays when clinically indicated, and the process is quick and
          comfortable.
        </p>

        <h3 className="text-xl font-semibold">Scale and clean</h3>
        <p>
          Our dental hygiene treatment is gentle and thorough. We use child-friendly tools and explain each step in a
          way that makes sense to young patients.
        </p>

        <h3 className="text-xl font-semibold">Aftercare and advice</h3>
        <p>
          We will always take the time to discuss your child's oral health, answer any questions, and recommend a recall
          schedule. If further treatment is indicated, we will provide a clear written quote and discuss options —
          including the use of remaining CDBS benefit.
        </p>

        <h3 className="text-xl font-semibold">Our bilingual team</h3>
        <p>
          Y3 Smiles Dental is proud to offer dental care in <strong>Turkish and Arabic</strong> as well as English. For
          many families in Broadmeadows, Glenroy, Campbellfield, and Meadow Heights, being able to communicate
          comfortably in your preferred language makes a meaningful difference — especially when explaining a child's
          dental needs.
        </p>
      </section>

      <div className="collage-grid" aria-label="Y3 Smiles CDBS clinic and dentist team collage">
        <img
          src="/clinic-pics/large/Y3 - Photo-99.jpg"
          alt="Y3 Smiles Dental — CDBS-registered clinic in Broadmeadows"
          className="collage-main"
          loading="lazy"
          decoding="async"
        />
        <div className="collage-stack">
          <img
            src="/DR%20Fatima%20Headshot%20New.png?v=20260421"
            alt="Dr Fatima Kurnaz — experienced in children's dentistry under CDBS"
            loading="lazy"
            decoding="async"
          />
          <img
            src="/optimized/new-dentist-1.webp"
            alt="Dr Pinar Geyik — gentle children's dental care at Y3 Smiles"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>

      <section className="space-y-6">
        <h2 className="text-2xl font-semibold">Frequently Asked Questions</h2>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold">Can I use CDBS at any dentist, or only at public clinics?</h3>
          <p>
            No — you are not limited to community dental clinics or public hospital dental services. The CDBS can be
            used at any <strong>registered participating private dental practice</strong>, including Y3 Smiles Dental.
            This means your child can receive care in a modern, welcoming private clinic without the long waitlists often
            associated with public dental services. Y3 Smiles accepts CDBS across all of our available appointment types
            for eligible children.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold">Does CDBS cover braces or orthodontic treatment?</h3>
          <p>
            No. The Child Dental Benefits Schedule is designed to fund{" "}
            <strong>preventive and basic dental care</strong> — not orthodontic treatment. Orthodontic services such as
            traditional braces, clear aligners, retainers, and palate expanders are not claimable under the CDBS. If
            your child needs orthodontic assessment, this would be a separate consultation and cost. Speak to our team
            and we can discuss the options available.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold">What if my child needs a filling — is that included?</h3>
          <p>
            Yes. <strong>Fillings are covered under the CDBS</strong>, subject to available benefit balance and clinical
            eligibility. This includes tooth-coloured (composite) fillings for visible teeth and amalgam fillings where
            applicable. If your child has a cavity identified during their check-up, we will explain the recommended
            treatment, provide a written quote, and confirm what portion will be covered by their CDBS balance before any
            work begins.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold">My child is nervous about the dentist. Can you help?</h3>
          <p>
            Absolutely. Managing dental anxiety in children is something our team has considerable experience with. We
            use a gentle, explain-and-show approach — always talking your child through what is happening before we do
            it. Our appointments are unhurried, our team is warm and patient, and we adapt the pace to suit each
            individual child. For families who speak Turkish or Arabic, our bilingual team members can communicate
            directly with your child in their preferred language, which can significantly reduce anxiety. If your child
            has significant dental anxiety, please mention this when booking so we can prepare accordingly.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold">Can I book online?</h3>
          <p>
            Yes. You can{" "}
            <Link to="/booking" className="text-primary hover:underline">
              book your child's CDBS appointment online
            </Link>{" "}
            at any time, 24 hours a day. Select "Children's Dentistry (CDBS)" as the service when prompted.
            Alternatively, call us on <strong>(03) 9022 4442</strong> and our reception team will be happy to assist. If
            you are unsure about your child's CDBS eligibility, just let us know when you book and we can check on the
            day via HICAPS.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Conclusion</h2>
        <p>
          The Child Dental Benefits Schedule exists to make dental care genuinely accessible for Australian families —
          but only families who know about it can use it. If your child is aged between 2 and 17 and your family
          receives Family Tax Benefit Part A or a qualifying Centrelink payment, there is a real chance your child has
          up to $1,158 in dental benefits available to them right now.
        </p>
        <p>
          At Y3 Smiles Dental, we are a participating CDBS provider serving families across Broadmeadows, Glenroy,
          Campbellfield, Craigieburn, Meadow Heights, Westmeadows, and the wider northern Melbourne region. Our
          bilingual team, HICAPS on-site Medicare claiming, and gentle child-friendly approach mean the experience is as
          easy and comfortable as possible for both you and your child.
        </p>
        <p>
          <strong>Check your child's eligibility today — and if they qualify, do not let those benefits go to waste.</strong>
        </p>
        <p>
          <Link to="/booking" className="text-primary hover:underline">
            Book a CDBS appointment online
          </Link>{" "}
          or call <strong>(03) 9022 4442</strong>. Same-week appointments are often available. For more information
          about our children's dental services, visit our{" "}
          <Link to="/treatments/childrens-dentistry" className="text-primary hover:underline">
            Children's Dentistry page
          </Link>
          .
        </p>
      </section>

      <div className="bg-neutral-50 border border-primary/10 rounded-2xl p-6 text-sm text-neutral-700">
        This article is for general educational purposes only. Please consult your dentist for advice specific to your
        situation.
      </div>

      <p className="text-sm text-neutral-500">
        <strong>Written by Dr Fatima Kurnaz DDS (MELB) | AHPRA Registration No. DEN0001925511</strong>
        <br />
        Y3 Smiles Dental
      </p>
    </NewsArticleLayout>
  );
};

export default CDBSGuidePage;
